import Prism from 'prismjs';
import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'prismjs/themes/prism-tomorrow.css';
// Import commonly used language syntaxes
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-sql';

interface RichMessageContentProps {
  content: string;
  className?: string;
}

/**
 * Renders message content with support for:
 * - Markdown (GitHub Flavored Markdown)
 * - Code blocks with syntax highlighting
 * - Inline code
 * - Links, images, lists, tables, etc.
 */
export function RichMessageContent({ content, className = '' }: RichMessageContentProps) {
  // Run Prism syntax highlighting after render
  useEffect(() => {
    Prism.highlightAll();
  }, [content]);

  // Simple heuristic: if content has markdown-like patterns, render as markdown
  // Otherwise render as plain text
  const hasMarkdown =
    content.includes('```') || // Code blocks
    content.includes('##') || // Headers
    content.includes('- ') || // Lists
    content.includes('* ') || // Lists
    content.includes('[') || // Links/images
    content.includes('|') || // Tables
    content.includes('`'); // Inline code

  if (!hasMarkdown) {
    // Plain text - preserve whitespace
    return <div className={`break-words whitespace-pre-wrap ${className}`}>{content}</div>;
  }

  return (
    <div className={`break-words ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom code block component with Prism highlighting
          code(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { node, inline, className, children, ...rest } = props as {
              node?: unknown;
              inline?: boolean;
              className?: string;
              children?: React.ReactNode;
            };
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';

            if (inline) {
              // Inline code
              return (
                <code className="px-1.5 py-0.5 rounded bg-slate-800 text-pink-400" {...rest}>
                  {children}
                </code>
              );
            }

            // Code block
            return (
              <div className="relative group">
                <pre className={`language-${language} rounded-lg overflow-x-auto`}>
                  <code className={`language-${language}`} {...rest}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },
          // Style links
          a(props) {
            const { children, href, ...rest } = props;
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
                {...rest}
              >
                {children}
              </a>
            );
          },
          // Style images
          img(props) {
            const { src, alt, ...rest } = props;
            return (
              <img
                src={src}
                alt={alt}
                className="rounded-lg max-w-full h-auto"
                loading="lazy"
                {...rest}
              />
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
